import useAsyncTask, {
    useLazyAsyncTask,
} from "@agrigate/shared/lib/hooks/use-async-task";
import { Box, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import "./SurveySubmissionForm.css";
import { toNumIfVal } from "@agrigate/ts-utils/lib/coercions";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { format } from "date-fns";
import type { ReactNode } from "react";
import { createContext } from "react";
import { MdCheckCircle, MdHourglassBottom } from "react-icons/md";
import type { SurveyQuestion } from "./SurveyBuilderForm";
import type { SurveyResponse } from "./SurveyFormGenerator";
import SurveyFormGenerator from "./SurveyFormGenerator";
import { getRPC } from "./utils/rpc";

interface SurveyConfigContextValue {
    surveyId: string;
}

export const SurveyConfigContext = createContext<SurveyConfigContextValue>({
    surveyId: "",
});

export default function SurveySubmissionForm(p: {
    id: string;
    assetBaseUrl: string;
    localeCode?: MaybeN<string>;
    submissionEnabled?: boolean;
}) {
    const [submittedAt, setSubmittedAt] = useState<MaybeN<Date>>(null);
    const getFormT = useAsyncTask(
        async () => {
            if (!p.id) return null;
            return await getRPC().surveys.findById({
                id: p.id,
                localeCode: p.localeCode,
            });
        },
        {
            deps: [p.id, p.localeCode],
        },
    );

    const getResponseT = useAsyncTask(
        async () => {
            if (!p.id) return null;
            const resp = await getRPC().surveys.findLatestResponseForUser({
                surveyFormId: p.id,
            });
            return resp?.data;
        },
        {
            deps: [p.id],
        },
    );

    const getCoinBalanceT = useAsyncTask(
        async () => {
            const res = await getRPC().rewards.findCoinsByUserId({
                userId: "$self",
            });
            return res.coins;
        },
        {
            deps: [],
        },
    );

    const redeemT = useLazyAsyncTask(
        async () => {
            const { redirectUrl } =
                await getRPC().rewards.initiateXoxodayRedemption({
                    rewardId: "XOXODAY_REDEMPTION",
                });
            window.location.href = redirectUrl;
        },
        {
            subject: "Reward",
            action: "redeem",
        },
    );

    const onSubmitSurvey = async (
        data: SurveyResponse[],
        isComplete?: MaybeN<boolean>,
    ) => {
        await getRPC().surveys.submitResponse({
            surveyFormId: p.id,
            response: { data },
            format: "custom",
            isComplete,
        });
    };

    const formConfig = getFormT.data?.form?.formConfig;

    const questions = useMemo<SurveyQuestion[]>(
        () => (formConfig ? JSON.parse(formConfig) : []),
        [formConfig],
    );

    if (!getFormT.data?.form) return null;

    if (submittedAt) {
        const rewardCoins = toNumIfVal(getFormT.data.rewardCoins) ?? 0;
        const coinBalance = getCoinBalanceT.data;
        return (
            <Box p={4} maxWidth="800px" margin="auto">
                <VStack gap={3} align="stretch" w="100%">
                    <HStack
                        bg="#DCFCE7"
                        borderWidth={1}
                        borderColor="#D4D4D8"
                        borderRadius="16px"
                        justify="center"
                        gap={3}
                        h="65px"
                        px={3}
                    >
                        <Icon
                            as={MdCheckCircle}
                            color="#2A8645"
                            boxSize="24px"
                        />
                        <Text
                            fontWeight="medium"
                            fontSize="16px"
                            lineHeight="24px"
                            color="black"
                            m={0}
                        >
                            Survey submitted successfully!
                        </Text>
                    </HStack>

                    <VStack
                        bg="#FAFAFA"
                        borderWidth={1}
                        borderColor="#D4D4D8"
                        borderRadius="20px"
                        p="20px"
                        gap="18px"
                        align="stretch"
                    >
                        <HStack gap={3} align="center">
                            <HStack
                                bg="#FBF0CE"
                                borderRadius="full"
                                justify="center"
                                boxSize="46px"
                                flexShrink={0}
                            >
                                <Icon
                                    as={MdHourglassBottom}
                                    color="#B07800"
                                    boxSize="24px"
                                />
                            </HStack>
                            <VStack gap="3px" align="stretch" flex={1} minW={0}>
                                <Text
                                    fontSize="18px"
                                    fontWeight="semibold"
                                    lineHeight="28px"
                                    color="#27272A"
                                    m={0}
                                >
                                    {rewardCoins
                                        ? `${rewardCoins} points in review`
                                        : "Response in review"}
                                </Text>
                                <Text
                                    fontSize="12px"
                                    lineHeight="16px"
                                    color="#858585"
                                    m={0}
                                >
                                    Survey · {getFormT.data.form.name}
                                </Text>
                            </VStack>
                        </HStack>

                        <Box h="1px" bg="#E4E4E7" w="100%" />

                        <VStack gap={0} align="stretch">
                            <TimelineStep
                                dotColor="#2A8645"
                                title="Answers submitted"
                                subtitle={`Today, ${format(
                                    submittedAt ?? new Date(),
                                    "h:mm a",
                                )}`}
                            />
                            <TimelineStep
                                dotColor="#E0A013"
                                titleWeight="medium"
                                title="Under review by our team"
                                subtitle="Usually approved within 24 hours"
                            />
                            <TimelineStep
                                isLast
                                titleColor="#A3A3A3"
                                title={
                                    rewardCoins
                                        ? `${rewardCoins} points added to your wallet`
                                        : "Your response is accepted"
                                }
                                subtitle="We'll notify you the moment it's done"
                            />
                        </VStack>
                    </VStack>

                    {coinBalance == null ? null : (
                        <VStack gap={3} align="stretch">
                            <Text
                                fontSize="14px"
                                fontWeight="semibold"
                                lineHeight="20px"
                                color="black"
                                m={0}
                            >
                                Reward Balance
                            </Text>
                            <VStack
                                bg="#FAFAE7"
                                borderWidth={1}
                                borderColor="#E4E4E4"
                                borderRadius="12px"
                                p={3}
                                gap="11px"
                                align="stretch"
                            >
                                <HStack justify="space-between">
                                    <Text
                                        fontSize="16px"
                                        fontWeight="semibold"
                                        lineHeight="24px"
                                        color="#9F4602"
                                        m={0}
                                    >
                                        {coinBalance} points
                                    </Text>
                                    <HStack
                                        bg="white"
                                        borderRadius="8px"
                                        p={2}
                                        justify="center"
                                        boxShadow="0px 2px 0.438px rgba(24, 24, 27, 0.2), 0px 1px 0.875px rgba(24, 24, 27, 0.1)"
                                    >
                                        <Text
                                            fontSize="14px"
                                            fontWeight="semibold"
                                            lineHeight="20px"
                                            color="#124A28"
                                            m={0}
                                        >
                                            = ₹{coinBalance}
                                        </Text>
                                    </HStack>
                                </HStack>
                                <HStack
                                    as="button"
                                    onClick={() => {
                                        if (redeemT.runState === "running")
                                            return;
                                        if (!coinBalance) {
                                            alert(
                                                "You don't have any points to redeem. Please revisit after earning some points.",
                                            );
                                            return;
                                        }
                                        redeemT.trigger();
                                    }}
                                    cursor="pointer"
                                    bg="#F0FDF4"
                                    borderWidth={1}
                                    borderColor="#4ADE80"
                                    borderRadius="8px"
                                    h="56px"
                                    px="20px"
                                    justify="center"
                                    w="100%"
                                >
                                    <Text
                                        fontSize="15.5px"
                                        fontWeight="semibold"
                                        color="#16A34A"
                                        m={0}
                                    >
                                        {redeemT.runState === "running"
                                            ? "Please wait…"
                                            : "Redeem your Rewards"}
                                    </Text>
                                </HStack>
                            </VStack>
                        </VStack>
                    )}

                    <HStack
                        as="button"
                        onClick={() => {
                            window.ReactNativeWebView?.postMessage(
                                JSON.stringify({ type: "back_to_home" }),
                            );
                        }}
                        cursor="pointer"
                        h="56px"
                        px="20px"
                        justify="center"
                        w="100%"
                        mt={2}
                    >
                        <Text
                            fontSize="15.5px"
                            fontWeight="semibold"
                            color="#C5C5C5"
                            m={0}
                        >
                            Back to home
                        </Text>
                    </HStack>
                </VStack>
            </Box>
        );
    }

    return (
        <Box background="white" p={4} maxWidth="800px" margin="auto" h={"100%"}>
            <SurveyConfigContext.Provider
                value={{
                    surveyId: p.id,
                }}
            >
                <SurveyFormGenerator
                    formName={getFormT.data.form.name}
                    questions={questions}
                    onSubmit={(data, isComplete) => {
                        onSubmitSurvey(data, isComplete);
                        if (isComplete) {
                            setSubmittedAt(new Date());
                        }
                    }}
                    response={getResponseT.data}
                    rewardCoins={toNumIfVal(getFormT.data.rewardCoins)}
                    surveyId={getFormT.data.form.id}
                    assetBaseUrl={p.assetBaseUrl}
                />
            </SurveyConfigContext.Provider>
        </Box>
    );
}

function TimelineStep(p: {
    title: ReactNode;
    subtitle: ReactNode;
    dotColor?: string;
    titleColor?: string;
    titleWeight?: "normal" | "medium";
    isLast?: boolean;
}) {
    return (
        <HStack gap={3} align="stretch">
            <VStack gap={0} align="center" w="14px" flexShrink={0}>
                <Box
                    mt="5px"
                    boxSize="12px"
                    borderRadius="full"
                    bg={p.dotColor ?? "white"}
                    borderWidth={p.dotColor ? 0 : "1.5px"}
                    borderColor="#D4D4D4"
                    flexShrink={0}
                />
                {p.isLast ? null : (
                    <Box flex={1} w="1px" bg="#E4E4E4" mt="4px" />
                )}
            </VStack>
            <VStack
                gap="2px"
                align="stretch"
                flex={1}
                minW={0}
                pb={p.isLast ? 0 : 4}
            >
                <Text
                    fontSize="14px"
                    lineHeight="20px"
                    fontWeight={p.titleWeight ?? "normal"}
                    color={p.titleColor ?? "#191919"}
                    m={0}
                >
                    {p.title}
                </Text>
                <Text fontSize="12px" lineHeight="16px" color="#9A9A9A" m={0}>
                    {p.subtitle}
                </Text>
            </VStack>
        </HStack>
    );
}
